/**
 * The packaged share experience: a themed preview card plus one-click channels.
 * Image generation is client-side (html-to-image) so this ships without any
 * backend. Channels that have a real web share-intent (WhatsApp, Telegram,
 * LinkedIn, Facebook, Mail) open directly; Instagram has no web intent so it
 * falls back to downloading the image, and "More" uses the OS share sheet.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { toPng } from 'html-to-image'
import QRCode from 'qrcode'
import { cn } from '@/lib/cn'
import { glassSurface } from '@/lib/glassSurface'
import { goldGradientButtonSm } from '@/lib/brandGradient'
import { useToast } from '@/components/ui/Toast'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import {
  buildSharePayload,
  canNativeShare,
  canNativeShareFiles,
  copyToClipboard,
  dataUrlToFile,
  downloadDataUrl,
  nativeShare,
  openChannel,
  shareFilename,
  type ShareChannel,
  type ShareSubject,
} from '@/lib/share'
import { ShareCard, SHARE_CARD_SIZE } from '@/components/share/ShareCard'
import {
  CloseIcon,
  DownloadIcon,
  FacebookIcon,
  InstagramIcon,
  LinkIcon,
  LinkedInIcon,
  MailIcon,
  NativeShareIcon,
  TelegramIcon,
  WhatsAppIcon,
} from '@/components/share/shareIcons'

const PREVIEW_WIDTH = 312

/**
 * html-to-image options shared by both capture paths. The card is styled
 * entirely with inline styles and carries its own font stack, so web-font
 * embedding is skipped; an empty `fontEmbedCSS` also stops html-to-image from
 * scanning document stylesheets, which throws (and logs) on the cross-origin
 * Google Fonts sheet.
 */
const CARD_CAPTURE_OPTIONS = {
  width: SHARE_CARD_SIZE,
  height: SHARE_CARD_SIZE,
  pixelRatio: 1,
  cacheBust: true,
  skipFonts: true,
  fontEmbedCSS: '',
} as const

type IntentChannel = Exclude<ShareChannel, 'copy' | 'native'>

const CHANNELS: {
  id: IntentChannel
  /** Screen-reader only — not shown in the UI. */
  ariaLabel: string
  Icon: (props: { className?: string }) => React.ReactElement
  /** Brand-tinted circular chip — soft filled background in both light and
   *  dark mode so every channel (including Email) reads as a consistent circle. */
  tint: string
}[] = [
  {
    id: 'whatsapp',
    ariaLabel: 'Share on WhatsApp',
    Icon: WhatsAppIcon,
    tint: 'bg-[#25D366]/15 text-[#1a9b50] dark:text-[#25D366]',
  },
  {
    id: 'telegram',
    ariaLabel: 'Share on Telegram',
    Icon: TelegramIcon,
    tint: 'bg-[#2AABEE]/15 text-[#1c8ec9] dark:text-[#2AABEE]',
  },
  {
    id: 'linkedin',
    ariaLabel: 'Share on LinkedIn',
    Icon: LinkedInIcon,
    tint: 'bg-[#0A66C2]/15 text-[#0A66C2] dark:bg-[#0A66C2]/20 dark:text-[#4596e6]',
  },
  {
    id: 'facebook',
    ariaLabel: 'Share on Facebook',
    Icon: FacebookIcon,
    tint: 'bg-[#1877F2]/15 text-[#1877F2] dark:bg-[#1877F2]/18 dark:text-[#4596e6]',
  },
  {
    id: 'email',
    ariaLabel: 'Share by email',
    Icon: MailIcon,
    tint: 'bg-zinc-500/15 text-zinc-600 dark:bg-white/10 dark:text-white/80',
  },
  {
    id: 'instagram',
    ariaLabel: 'Share on Instagram',
    Icon: InstagramIcon,
    tint: 'bg-[#E1306C]/15 text-[#E1306C] dark:bg-[#E1306C]/18 dark:text-[#f06595]',
  },
]

export function ShareModal({
  open,
  onClose,
  subject,
}: {
  open: boolean
  onClose: () => void
  subject: ShareSubject | null
}) {
  const { showToast } = useToast()
  useBodyScrollLock(open)
  const cardRef = useRef<HTMLDivElement>(null)
  const [imageDataUrl, setImageDataUrl] = useState<string | null>(null)
  const [generating, setGenerating] = useState(false)
  // null = QR not attempted yet; { url } = attempted (url is null on failure).
  // The card capture waits for this so the QR is baked into the image.
  const [qr, setQr] = useState<{ url: string | null } | null>(null)

  const payload = useMemo(
    () => (subject ? buildSharePayload(subject) : null),
    [subject],
  )

  // Esc-to-close, matching the app's other modals.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  // Generate the QR for the share URL. Encoded dark-on-white so it stays
  // scannable against the card's white QR plate.
  useEffect(() => {
    if (!open || !payload) {
      setQr(null)
      return
    }
    let cancelled = false
    setQr(null)
    QRCode.toDataURL(payload.url, {
      width: 264,
      margin: 1,
      color: { dark: '#0b0b10', light: '#ffffff' },
    })
      .then((url) => {
        if (!cancelled) setQr({ url })
      })
      .catch(() => {
        if (!cancelled) setQr({ url: null })
      })
    return () => {
      cancelled = true
    }
  }, [open, payload])

  // Rasterize the card once the modal opens AND the QR has resolved, so the
  // code is baked into the image. Regenerates whenever the subject changes.
  useEffect(() => {
    if (!open || !subject) {
      setImageDataUrl(null)
      return
    }
    // Wait for the QR attempt to settle before capturing.
    if (qr === null) return
    let cancelled = false
    setGenerating(true)
    const run = async () => {
      try {
        // Fonts must be ready or the first capture renders with a fallback.
        if (document.fonts?.ready) await document.fonts.ready
        await new Promise((r) => requestAnimationFrame(() => r(null)))
        if (cancelled || !cardRef.current) return
        const url = await toPng(cardRef.current, CARD_CAPTURE_OPTIONS)
        if (!cancelled) setImageDataUrl(url)
      } catch {
        if (!cancelled) setImageDataUrl(null)
      } finally {
        if (!cancelled) setGenerating(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [open, subject, qr])

  const ensureImage = useCallback(async (): Promise<string | null> => {
    if (imageDataUrl) return imageDataUrl
    if (!cardRef.current) return null
    try {
      setGenerating(true)
      if (document.fonts?.ready) await document.fonts.ready
      const url = await toPng(cardRef.current, CARD_CAPTURE_OPTIONS)
      setImageDataUrl(url)
      return url
    } catch {
      return null
    } finally {
      setGenerating(false)
    }
  }, [imageDataUrl])

  const handleDownload = useCallback(async () => {
    if (!subject) return
    const url = await ensureImage()
    if (!url) {
      showToast('Could not generate the image. Try again.', 'error')
      return
    }
    downloadDataUrl(url, shareFilename(subject))
    showToast('Image saved to your downloads.', 'success')
  }, [subject, ensureImage, showToast])

  const handleChannel = useCallback(
    async (channel: IntentChannel) => {
      if (!payload || !subject) return
      if (channel === 'instagram') {
        // Instagram has no content-prefill web URL, so on mobile route through
        // the native share with the card image attached — that lands the user
        // on Instagram's own "share to" screen (Story/Direct/Feed) with the
        // image ready. Desktop has no file-share, so fall back to opening IG.
        const url = await ensureImage()
        if (url) {
          try {
            const file = await dataUrlToFile(url, shareFilename(subject))
            if (canNativeShareFiles([file])) {
              const shared = await nativeShare({
                title: payload.title,
                text: payload.text,
                url: payload.url,
                files: [file],
              })
              if (shared) return
            }
          } catch {
            // fall through to opening Instagram directly
          }
        }
        window.open(
          'https://www.instagram.com/',
          '_blank',
          'noopener,noreferrer',
        )
        return
      }
      openChannel(channel, payload)
    },
    [payload, subject, ensureImage],
  )

  const handleCopyLink = useCallback(async () => {
    if (!payload) return
    const ok = await copyToClipboard(payload.url)
    showToast(
      ok ? 'Link copied to clipboard.' : 'Could not copy the link.',
      ok ? 'success' : 'error',
    )
  }, [payload, showToast])

  const handleNativeShare = useCallback(async () => {
    if (!payload || !subject) return
    const url = await ensureImage()
    let files: File[] | undefined
    if (url) {
      try {
        const file = await dataUrlToFile(url, shareFilename(subject))
        if (canNativeShareFiles([file])) files = [file]
      } catch {
        files = undefined
      }
    }
    const shared = await nativeShare({
      title: payload.title,
      text: payload.text,
      url: payload.url,
      files,
    })
    if (!shared) {
      // User dismissed the sheet or it is unsupported; copy as a fallback.
      const ok = await copyToClipboard(`${payload.text}\n\n${payload.url}`)
      if (ok) showToast('Share text copied to clipboard.', 'success')
    }
  }, [payload, subject, ensureImage, showToast])

  if (!open || !subject || !payload) return null

  const heading =
    subject.kind === 'idea'
      ? 'Share your idea'
      : "You're Moving Up The Leaderboard"
  const blurb =
    subject.kind === 'idea'
      ? 'Share your report and gather more perspectives.'
      : 'Let your network know about your impact.'

  const scale = PREVIEW_WIDTH / SHARE_CARD_SIZE

  const modal = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto bg-[#050505]/65 px-4 py-6 backdrop-blur-[20px]">
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 cursor-default"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
        className={cn(
          'relative my-auto box-border flex w-full max-w-[min(440px,calc(100vw-2rem))] shrink-0 flex-col rounded-2xl px-6 pt-6 pb-8 text-white shadow-2xl',
          glassSurface,
        )}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 grid h-7 w-7 place-items-center rounded-full border border-white/10 bg-white/5 text-slate-200 transition hover:bg-white/10 hover:text-white"
        >
          <CloseIcon className="h-3 w-3" />
        </button>

        <h2 id="share-modal-title" className="text-lg font-semibold">
          {heading}
        </h2>
        <p className="mt-1 text-[13px] leading-relaxed text-white/55">
          {blurb}
        </p>

        {/* Preview — the same artwork that gets rasterized, scaled to fit. */}
        <div className="mt-4 flex justify-center">
          <div
            className="relative overflow-hidden rounded-2xl border border-white/10"
            style={{ width: PREVIEW_WIDTH, height: PREVIEW_WIDTH }}
          >
            <div
              style={{
                width: SHARE_CARD_SIZE,
                height: SHARE_CARD_SIZE,
                transform: `scale(${scale})`,
                transformOrigin: 'top left',
              }}
            >
              <ShareCard subject={subject} qrDataUrl={qr?.url ?? null} />
            </div>
            {generating && (
              <div className="absolute inset-0 grid place-items-center bg-[#0b0b10]/55">
                <span className="h-6 w-6 animate-spin rounded-full border-2 border-white/20 border-t-[#E0A263]" />
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 space-y-5">
          {/* Social channels — icon-only circles (labels are aria-only). */}
          <div className="flex flex-nowrap items-center justify-center gap-2.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] sm:flex-wrap sm:gap-5 sm:overflow-visible sm:pb-0 [&::-webkit-scrollbar]:hidden">
            {CHANNELS.map(({ id, ariaLabel, Icon, tint }) => (
              <button
                key={id}
                type="button"
                aria-label={ariaLabel}
                title={ariaLabel}
                onClick={() => handleChannel(id)}
                className={cn(
                  'grid h-10 w-10 shrink-0 place-items-center rounded-full transition sm:h-11 sm:w-11',
                  'hover:brightness-110 active:scale-95',
                  tint,
                )}
              >
                <Icon className="h-[18px] w-[18px] sm:h-5 sm:w-5" />
              </button>
            ))}
          </div>

          {/* Secondary actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCopyLink}
              className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-4 py-3 text-[13px] font-medium text-white transition hover:bg-white/[0.08]"
            >
              <LinkIcon className="h-4 w-4 shrink-0" />
              Copy link
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={generating}
              className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full border border-white/12 bg-white/[0.04] px-4 py-3 text-[13px] font-medium text-white transition hover:bg-white/[0.08] disabled:opacity-50"
            >
              <DownloadIcon className="h-4 w-4 shrink-0" />
              Download
            </button>
          </div>

          {canNativeShare() && (
            <button
              type="button"
              onClick={handleNativeShare}
              className={cn(
                'flex min-h-[44px] w-full items-center justify-center gap-2 px-4 py-3 text-[13px] font-semibold',
                goldGradientButtonSm,
              )}
            >
              <NativeShareIcon className="h-4 w-4 shrink-0" />
              More sharing options
            </button>
          )}
        </div>
      </div>

      {/* Off-screen true-size card handed to html-to-image. */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          left: -99999,
          top: 0,
          pointerEvents: 'none',
          opacity: 0,
        }}
      >
        <ShareCard
          ref={cardRef}
          subject={subject}
          qrDataUrl={qr?.url ?? null}
        />
      </div>
    </div>
  )

  if (typeof document === 'undefined') return modal
  return createPortal(modal, document.body)
}
